import type { ActionType, ProColumns } from "@ant-design/pro-components";
import { Segmented, Tag } from "antd";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { readAdminStandardPageSize } from "./admin-table";
import {
  ENVIRONMENT_IMAGE_PICKER_KINDS,
  type EnvironmentImagePickerKind,
  type EnvironmentImagePickerRecordBase,
  normalizeAllowedImageKinds,
  readEnvironmentImagePickerKind,
} from "./environment-image-picker-contract";
import {
  type EnvironmentImagePickerControlledFilters,
  getEnvironmentImagePickerArchitectureTagColor,
  getEnvironmentImagePickerCategoryTagColor,
  getEnvironmentImagePickerShareModeTagColor,
  getEnvironmentImagePickerStatusTagColor,
  normalizeEnvironmentImagePickerAllowedScopes,
  readEnvironmentImagePickerFilterValues,
  renderEnvironmentImagePickerCellText,
} from "./environment-image-picker-utils";
import { buildLocalizedTextHeaderFilter } from "./localized-text-header-filter";
import { SharedAssetPickerModal, type SharedAssetPickerScope } from "./shared-asset-picker-modal";

export type {
  EnvironmentImagePickerKind,
  EnvironmentImagePickerRecordBase,
} from "./environment-image-picker-contract";
export {
  ENVIRONMENT_IMAGE_PICKER_KINDS,
  formatEnvironmentImagePickerDisplay,
  isEnvironmentImagePickerKind,
  normalizeAllowedImageKinds,
  readEnvironmentImagePickerKind,
  readEnvironmentImagePickerRefId,
} from "./environment-image-picker-contract";

export type EnvironmentImagePickerColumnPreset = "mixed" | "application" | "container";

export type EnvironmentImagePickerFilterValues = {
  code?: string;
  name?: string;
  description?: string;
  category?: string;
  usageType?: string;
  status?: string;
  shareMode?: string;
};

export type EnvironmentImagePickerRequestParams = {
  imageKind: EnvironmentImagePickerKind;
  scope: SharedAssetPickerScope;
  filters: EnvironmentImagePickerFilterValues;
  page: number;
  pageSize: number;
};

export type EnvironmentImagePickerRequestResult<T extends EnvironmentImagePickerRecordBase> = {
  items: T[];
  total: number;
};

export type EnvironmentImagePickerLabels = {
  cancelText: ReactNode;
  scopeLabels: Record<SharedAssetPickerScope, ReactNode>;
  searchPlaceholder: string;
  sourceLabels: Record<EnvironmentImagePickerKind, ReactNode>;
  columnTitles: {
    code: ReactNode;
    name: ReactNode;
    sourceType: ReactNode;
    identifier: ReactNode;
    category: ReactNode;
    architecture: ReactNode;
    usageType: ReactNode;
    status: ReactNode;
    shareMode: ReactNode;
    defaultSpec: ReactNode;
    description: ReactNode;
  };
  filters: {
    name: string;
  };
  messages: {
    loadFailed: string;
    selectFirst: string;
    filterSearch: string;
    filterReset: string;
  };
  getCategoryLabel: (value?: string | null) => ReactNode;
  getUsageTypeLabel: (value?: string | null) => ReactNode;
  getStatusLabel: (value?: string | null) => ReactNode;
  getShareModeLabel: (value?: string | null) => ReactNode;
  categoryValueEnum: Record<string, { text: ReactNode }>;
  usageTypeValueEnum: Record<string, { text: ReactNode }>;
  statusValueEnum: Record<string, { text: ReactNode }>;
  shareModeValueEnum: Record<string, { text: ReactNode }>;
};

export type EnvironmentImagePickerModalProps<T extends EnvironmentImagePickerRecordBase> = {
  open: boolean;
  title: ReactNode;
  submitText: ReactNode;
  persistenceKey: string;
  request: (
    params: EnvironmentImagePickerRequestParams,
  ) => Promise<EnvironmentImagePickerRequestResult<T>>;
  labels: EnvironmentImagePickerLabels;
  allowedScopes?: readonly SharedAssetPickerScope[];
  allowedImageKinds?: readonly EnvironmentImagePickerKind[];
  defaultImageKind?: EnvironmentImagePickerKind;
  selectedRowKey?: string | number | null;
  selectedRecord?: T | null;
  onSelectionChange: (record: T | null, key: string | number | null) => void;
  onClose: () => void;
  onSubmit: (selected: T) => Promise<boolean> | boolean;
  onError?: (message: string) => void;
  onWarning?: (message: string) => void;
  columnPreset?: EnvironmentImagePickerColumnPreset;
  rowKey?: string | ((record: T) => string | number);
  width?: number | string;
  tableLayout?: "auto" | "fixed";
};

export function EnvironmentImagePickerModal<T extends EnvironmentImagePickerRecordBase>({
  open,
  title,
  submitText,
  persistenceKey,
  request,
  labels,
  allowedScopes,
  allowedImageKinds = ENVIRONMENT_IMAGE_PICKER_KINDS,
  defaultImageKind,
  selectedRowKey,
  selectedRecord,
  onSelectionChange,
  onClose,
  onSubmit,
  onError,
  onWarning,
  columnPreset = "mixed",
  rowKey = "id",
  width,
  tableLayout = "auto",
}: EnvironmentImagePickerModalProps<T>) {
  const actionRef = useRef<ActionType | undefined>(undefined);
  const normalizedAllowedScopes = normalizeEnvironmentImagePickerAllowedScopes(allowedScopes);
  const normalizedAllowedImageKinds = normalizeAllowedImageKinds(allowedImageKinds);
  if (defaultImageKind && !normalizedAllowedImageKinds.includes(defaultImageKind)) {
    throw new Error(
      `ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: defaultImageKind ${defaultImageKind} is not in allowedImageKinds`,
    );
  }
  const initialImageKind = defaultImageKind ?? normalizedAllowedImageKinds[0];
  const initialScope = normalizedAllowedScopes[0];
  if (initialImageKind === undefined || initialScope === undefined) {
    throw new Error(
      "ENVIRONMENT_IMAGE_PICKER_CONTRACT_INVALID: normalized options must not be empty",
    );
  }
  const [activeScope, setActiveScope] = useState<SharedAssetPickerScope>(initialScope);
  const [activeImageKind, setActiveImageKind] =
    useState<EnvironmentImagePickerKind>(initialImageKind);
  const [filterValues, setFilterValues] = useState<EnvironmentImagePickerFilterValues>({});
  const onSelectionChangeRef = useRef(onSelectionChange);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    if (!open) return;
    setActiveScope(initialScope);
    setActiveImageKind(initialImageKind);
    setFilterValues({});
    onSelectionChangeRef.current(null, null);
  }, [initialImageKind, initialScope, open]);

  const columns = useMemo<ProColumns<T>[]>(() => {
    const isContainerPreset = columnPreset === "container";
    const baseColumns: ProColumns<T>[] = [
      {
        title: labels.columnTitles.code,
        dataIndex: "ref_code",
        render: (_, record) =>
          renderEnvironmentImagePickerCellText(record.ref_code, { code: isContainerPreset }),
        ...buildLocalizedTextHeaderFilter(labels.searchPlaceholder, {
          search: labels.messages.filterSearch,
          reset: labels.messages.filterReset,
        }),
        filteredValue: filterValues.code ? [filterValues.code] : null,
      },
      {
        title: labels.columnTitles.name,
        dataIndex: "ref_name",
        ellipsis: isContainerPreset,
        render: (_, record) =>
          isContainerPreset
            ? renderEnvironmentImagePickerCellText(record.ref_name)
            : record.ref_name,
        ...buildLocalizedTextHeaderFilter(labels.filters.name, {
          search: labels.messages.filterSearch,
          reset: labels.messages.filterReset,
        }),
        filteredValue: filterValues.name ? [filterValues.name] : null,
      },
    ];

    if (columnPreset === "mixed") {
      baseColumns.push({
        title: labels.columnTitles.sourceType,
        dataIndex: "image_kind",
        render: (_, record) => (
          <Tag>{labels.sourceLabels[readEnvironmentImagePickerKind(record)]}</Tag>
        ),
      });
    }

    if (isContainerPreset) {
      baseColumns.push({
        title: labels.columnTitles.identifier,
        dataIndex: "description",
        ellipsis: true,
        render: (_, record) => renderEnvironmentImagePickerCellText(record.description),
        ...buildLocalizedTextHeaderFilter(labels.searchPlaceholder, {
          search: labels.messages.filterSearch,
          reset: labels.messages.filterReset,
        }),
        filteredValue: filterValues.description ? [filterValues.description] : null,
      });
    }

    baseColumns.push({
      title: labels.columnTitles.category,
      dataIndex: "category",
      valueType: "select",
      filters: true,
      valueEnum: labels.categoryValueEnum,
      filteredValue: filterValues.category ? [filterValues.category] : null,
      render: (_, record) =>
        isContainerPreset ? (
          <Tag color={getEnvironmentImagePickerCategoryTagColor(record.category)}>
            {labels.getCategoryLabel(record.category)}
          </Tag>
        ) : (
          labels.getCategoryLabel(record.category)
        ),
    });

    if (isContainerPreset) {
      baseColumns.push(
        {
          title: labels.columnTitles.architecture,
          dataIndex: "architecture",
          render: (_, record) => (
            <Tag color={getEnvironmentImagePickerArchitectureTagColor(record.architecture)}>
              {record.architecture}
            </Tag>
          ),
        },
        {
          title: labels.columnTitles.usageType,
          dataIndex: "usage_type",
          valueType: "select",
          filters: true,
          valueEnum: labels.usageTypeValueEnum,
          filteredValue: filterValues.usageType ? [filterValues.usageType] : null,
          render: (_, record) => <Tag>{labels.getUsageTypeLabel(record.usage_type)}</Tag>,
        },
      );
    }

    baseColumns.push({
      title: labels.columnTitles.status,
      dataIndex: "status",
      valueType: "select",
      filters: true,
      valueEnum: labels.statusValueEnum,
      filteredValue: filterValues.status ? [filterValues.status] : null,
      render: (_, record) => (
        <Tag color={getEnvironmentImagePickerStatusTagColor(record.status)}>
          {labels.getStatusLabel(record.status)}
        </Tag>
      ),
    });

    if (isContainerPreset) {
      baseColumns.push({
        title: labels.columnTitles.shareMode,
        dataIndex: "share_mode",
        valueType: "select",
        filters: true,
        valueEnum: labels.shareModeValueEnum,
        filteredValue: filterValues.shareMode ? [filterValues.shareMode] : null,
        render: (_, record) => (
          <Tag color={getEnvironmentImagePickerShareModeTagColor(record.share_mode)}>
            {labels.getShareModeLabel(record.share_mode)}
          </Tag>
        ),
      });
    } else if (columnPreset === "application") {
      baseColumns.push({
        title: labels.columnTitles.defaultSpec,
        dataIndex: "default_spec_title",
      });
    }

    if (!isContainerPreset) {
      baseColumns.push({
        title: labels.columnTitles.description,
        dataIndex: "description",
      });
    }

    return baseColumns;
  }, [
    columnPreset,
    filterValues.category,
    filterValues.code,
    filterValues.description,
    filterValues.name,
    filterValues.shareMode,
    filterValues.status,
    filterValues.usageType,
    labels,
  ]);

  const imageKindHeader =
    normalizedAllowedImageKinds.length > 1 ? (
      <Segmented<EnvironmentImagePickerKind>
        block
        value={activeImageKind}
        onChange={(value) => {
          setActiveImageKind(value);
          setFilterValues({});
          onSelectionChange(null, null);
          actionRef.current?.reloadAndRest?.();
        }}
        options={normalizedAllowedImageKinds.map((kind) => ({
          label: labels.sourceLabels[kind],
          value: kind,
        }))}
        style={{ marginBottom: 12 }}
      />
    ) : undefined;

  return (
    <SharedAssetPickerModal<T>
      title={title}
      open={open}
      actionRef={actionRef}
      activeScope={activeScope}
      allowedScopes={normalizedAllowedScopes}
      tableKey={`${activeScope}:${activeImageKind}`}
      persistenceKey={persistenceKey}
      header={imageKindHeader}
      onScopeChange={(scope) => {
        setActiveScope(scope);
        setFilterValues({});
        onSelectionChange(null, null);
      }}
      scopeLabels={labels.scopeLabels}
      columns={columns}
      request={async (tableParams, _sorter, filters) => {
        try {
          const normalizedFilters = filters as EnvironmentImagePickerControlledFilters;
          const nextFilterValues = readEnvironmentImagePickerFilterValues(normalizedFilters);
          setFilterValues(nextFilterValues);
          const response = await request({
            imageKind: activeImageKind,
            scope: activeScope,
            filters: nextFilterValues,
            page: Math.max(1, Number(tableParams.current) || 1),
            pageSize: readAdminStandardPageSize(tableParams.pageSize),
          });
          return { data: response.items, total: response.total, success: true };
        } catch (error) {
          onError?.(error instanceof Error ? error.message : labels.messages.loadFailed);
          return { data: [], total: 0, success: false };
        }
      }}
      onClose={onClose}
      submitText={submitText}
      cancelText={labels.cancelText}
      onSubmit={async () => {
        if (!selectedRecord) {
          onWarning?.(labels.messages.selectFirst);
          return false;
        }
        return onSubmit(selectedRecord);
      }}
      rowKey={rowKey}
      selectedRowKeys={
        selectedRowKey !== null && selectedRowKey !== undefined ? [selectedRowKey] : []
      }
      onSelectionChange={(keys, rows) => {
        const nextKey = keys[0] ?? null;
        onSelectionChange(rows[0] ?? null, nextKey);
      }}
      rowSelectionType="radio"
      tableLayout={tableLayout}
      {...(width === undefined ? {} : { width })}
    />
  );
}
